import React, { memo, ReactNode, useEffect, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { RegionAPI } from '@business/api/region';
import { InfoBanner } from '@modules/common/InfoBanner';

// The account's Google Maps integration key, fetched once per page load:
// useJsApiLoader refuses to load the script a second time with other options.
let apiKeyRequest: Promise<string | null> | null = null;
function loadApiKey() {
  if (!apiKeyRequest) {
    apiKeyRequest = RegionAPI.fetchMapConfig()
      .toPromise()
      .then(config => config?.apiKey || null)
      .catch(() => {
        apiKeyRequest = null;
        return null;
      });
  }
  return apiKeyRequest;
}

// Google reports a rejected key (wrong referrer, Maps JavaScript API not
// enabled) only through this global, after the script has loaded fine.
let authFailed = false;
const authFailureListeners = new Set<() => void>();
(window as any).gm_authFailure = () => {
  authFailed = true;
  authFailureListeners.forEach(listener => listener());
};

const loading = <div className="text-muted font-size-12 py-3">Loading map…</div>;

export interface GoogleMapsLoaderProps {
  children: () => ReactNode;
}

/** Renders children once the Maps JavaScript API is ready, or explains why it can't be. */
export const GoogleMapsLoader = memo(({ children }: GoogleMapsLoaderProps) => {
  const [apiKey, setApiKey] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    loadApiKey().then(key => alive && setApiKey(key));
    return () => {
      alive = false;
    };
  }, []);

  if (apiKey === undefined) return loading;
  if (!apiKey) {
    return (
      <InfoBanner intent="warning" noMargin>
        Add a Google Maps API key in Settings → Integrations to show the map.
      </InfoBanner>
    );
  }
  return <ScriptLoader apiKey={apiKey}>{children}</ScriptLoader>;
});

const ScriptLoader = memo(({ apiKey, children }: GoogleMapsLoaderProps & { apiKey: string }) => {
  const { isLoaded, loadError } = useJsApiLoader({ id: 'gc-google-maps', googleMapsApiKey: apiKey });
  const [authError, setAuthError] = useState(authFailed);

  useEffect(() => {
    const listener = () => setAuthError(true);
    authFailureListeners.add(listener);
    return () => {
      authFailureListeners.delete(listener);
    };
  }, []);

  if (loadError || authError) {
    return (
      <InfoBanner intent="danger" noMargin>
        {"Google Maps couldn't load with this account's API key. Make sure the key allows the Maps JavaScript API on this site."}
      </InfoBanner>
    );
  }
  if (!isLoaded) return loading;
  return <>{children()}</>;
});
